/**
 * 选图闸门。弹窗打开之前拒绝：非图片、HEIC/HEIF、超过 20MB、GIF 超过 60 帧、解不开的字节。
 * 2MB / 8MB 是导出之后的上限，不在这里。
 */

export const AVATAR_INPUT_MAX_BYTES = 20 * 1024 * 1024;
export const AVATAR_STATIC_MAX_BYTES = 2 * 1024 * 1024;
export const AVATAR_GIF_MAX_BYTES = 8 * 1024 * 1024;
export const AVATAR_GIF_MAX_FRAMES = 60;
export const AVATAR_JPEG_QUALITY = 0.92;

const IMAGE_EXT_RE = /\.(jpe?g|png|gif|webp|bmp|avif|tiff?)$/i;
const HEIC_EXT_RE = /\.(heic|heif|heics|heifs)$/i;
const GIF_EXT_RE = /\.gif$/i;

export type AvatarPickProblem =
  | 'notImage'
  | 'tooLarge'
  | 'tooManyFrames'
  | 'undecodable';

export type AvatarPick =
  | {
      ok: true;
      kind: 'static' | 'gif';
      width: number;
      height: number;
    }
  | { ok: false; problem: AvatarPickProblem; sizeMb?: number };

function extensionOf(name: string): boolean {
  return IMAGE_EXT_RE.test(name);
}

export function isHeicAvatarFile(file: {
  name: string;
  type: string;
}): boolean {
  const mime = file.type.toLowerCase();
  if (mime === 'image/heic' || mime === 'image/heif') {
    return true;
  }
  return HEIC_EXT_RE.test(file.name);
}

export function isGifAvatarFile(file: { name: string; type: string }): boolean {
  return file.type.toLowerCase() === 'image/gif' || GIF_EXT_RE.test(file.name);
}

function isImageFile(file: { name: string; type: string }): boolean {
  if (isHeicAvatarFile(file)) {
    return false;
  }
  if (file.type.toLowerCase().startsWith('image/')) {
    return true;
  }
  return extensionOf(file.name);
}

/**
 * 数 GIF 图像描述符。只扫块，不解码像素。结构不合法时返回 `null`。
 */
export function countGifFrames(bytes: Uint8Array): number | null {
  if (bytes.length < 13) {
    return null;
  }
  const sig = String.fromCharCode(
    bytes[0] ?? 0,
    bytes[1] ?? 0,
    bytes[2] ?? 0,
    bytes[3] ?? 0,
    bytes[4] ?? 0,
    bytes[5] ?? 0,
  );
  if (sig !== 'GIF87a' && sig !== 'GIF89a') {
    return null;
  }
  let offset = 13;
  const packed = bytes[10] ?? 0;
  if ((packed & 0x80) !== 0) {
    offset += 3 * (2 << (packed & 0x07));
  }
  let frames = 0;
  while (offset < bytes.length) {
    const marker = bytes[offset];
    if (marker === 0x3b) {
      return frames;
    }
    if (marker === 0x2c) {
      if (offset + 10 > bytes.length) {
        return null;
      }
      const imagePacked = bytes[offset + 9] ?? 0;
      offset += 10;
      if ((imagePacked & 0x80) !== 0) {
        offset += 3 * (2 << (imagePacked & 0x07));
      }
      offset += 1;
      const next = skipSubBlocks(bytes, offset);
      if (next === null) {
        return null;
      }
      offset = next;
      frames += 1;
      continue;
    }
    if (marker === 0x21) {
      offset += 2;
      const next = skipSubBlocks(bytes, offset);
      if (next === null) {
        return null;
      }
      offset = next;
      continue;
    }
    return null;
  }
  return null;
}

function skipSubBlocks(bytes: Uint8Array, offset: number): number | null {
  while (offset < bytes.length) {
    const size = bytes[offset] ?? 0;
    offset += 1;
    if (size === 0) {
      return offset;
    }
    offset += size;
  }
  return null;
}

/**
 * Netscape 循环次数。没有这段扩展时返回 `null`（只播一遍）。
 * `0` 是无限循环。
 */
export function gifLoopCount(bytes: Uint8Array): number | null {
  const label = [
    0x4e, 0x45, 0x54, 0x53, 0x43, 0x41, 0x50, 0x45, 0x32, 0x2e, 0x30,
  ];
  for (let offset = 0; offset + 18 < bytes.length; offset += 1) {
    if (
      bytes[offset] !== 0x21 ||
      bytes[offset + 1] !== 0xff ||
      bytes[offset + 2] !== 11
    ) {
      continue;
    }
    let matches = true;
    for (let index = 0; index < label.length; index += 1) {
      if (bytes[offset + 3 + index] !== label[index]) {
        matches = false;
        break;
      }
    }
    if (!matches || bytes[offset + 14] !== 3 || bytes[offset + 15] !== 1) {
      continue;
    }
    return (bytes[offset + 16] ?? 0) | ((bytes[offset + 17] ?? 0) << 8);
  }
  return null;
}

export function gifScreenSize(
  bytes: Uint8Array,
): { width: number; height: number } | null {
  if (countGifFrames(bytes) === null || bytes.length < 10) {
    return null;
  }
  const width = (bytes[6] ?? 0) | ((bytes[7] ?? 0) << 8);
  const height = (bytes[8] ?? 0) | ((bytes[9] ?? 0) << 8);
  if (width < 1 || height < 1) {
    return null;
  }
  return { width, height };
}

export function avatarExportName(
  original: string,
  ext: 'png' | 'jpg' | 'gif',
): string {
  const slash = Math.max(original.lastIndexOf('/'), original.lastIndexOf('\\'));
  const base = slash >= 0 ? original.slice(slash + 1) : original;
  const dot = base.lastIndexOf('.');
  const stem = dot > 0 ? base.slice(0, dot) : dot === 0 ? '' : base;
  return `${stem.length > 0 ? stem : 'avatar'}.${ext}`;
}

export function avatarExportLimit(kind: 'static' | 'gif'): {
  bytes: number;
  sizeMb: number;
} {
  if (kind === 'gif') {
    return { bytes: AVATAR_GIF_MAX_BYTES, sizeMb: 8 };
  }
  return { bytes: AVATAR_STATIC_MAX_BYTES, sizeMb: 2 };
}

type BitmapLike = { width: number; height: number; close?: () => void };

async function decodeStaticSize(file: Blob): Promise<BitmapLike> {
  const bitmap = await createImageBitmap(file, {
    imageOrientation: 'from-image',
  });
  return bitmap;
}

export async function inspectAvatarFile(file: File): Promise<AvatarPick> {
  if (!isImageFile(file)) {
    return { ok: false, problem: 'notImage' };
  }
  if (file.size > AVATAR_INPUT_MAX_BYTES) {
    return { ok: false, problem: 'tooLarge', sizeMb: 20 };
  }
  try {
    if (isGifAvatarFile(file)) {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const frames = countGifFrames(bytes);
      if (frames === null || frames < 1) {
        return { ok: false, problem: 'undecodable' };
      }
      if (frames > AVATAR_GIF_MAX_FRAMES) {
        return { ok: false, problem: 'tooManyFrames' };
      }
      const screen = gifScreenSize(bytes);
      if (!screen) {
        return { ok: false, problem: 'undecodable' };
      }
      if (frames === 1) {
        const bitmap = await decodeStaticSize(file);
        const size = { width: bitmap.width, height: bitmap.height };
        bitmap.close?.();
        if (size.width < 1 || size.height < 1) {
          return { ok: false, problem: 'undecodable' };
        }
        return { ok: true, kind: 'static', ...size };
      }
      return { ok: true, kind: 'gif', ...screen };
    }
    const bitmap = await decodeStaticSize(file);
    const size = { width: bitmap.width, height: bitmap.height };
    bitmap.close?.();
    if (size.width < 1 || size.height < 1) {
      return { ok: false, problem: 'undecodable' };
    }
    return { ok: true, kind: 'static', ...size };
  } catch {
    return { ok: false, problem: 'undecodable' };
  }
}
